import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import React from "react";
import {
  searchQuotesSortingToPropertySortings,
  searchQuotesSortingToQuoteTemplatesSorting,
} from "../../templates/helpers.js";
import {
  TemplateBase,
  TemplateListItem,
  TemplatesCollectionView,
  TemplatesDisplay,
} from "../../templates/quote/TemplatesDisplay.js";
import { TemplatesQuoteOverviewModal } from "../../templates/quote/TemplatesQuoteOverviewModal.js";
import { useTemplatesQueryVariablesInState } from "../../templates/quote/useTemplatesQueryVariables.js";
import { useSetItemTemplatePlaceholderTemplateMutation } from "../buttons/TemplatePlaceholderSelectTemplateButton.generated.js";
import {
  filterDefinitionsToFormValues,
  sortingDefinitionsToFormValues,
} from "../templatePlaceholderSearchDefaults.js";
import {
  PLACEHOLDER_SELECTION_PAGE_SIZE,
  PLACEHOLDER_SELECTION_PAGE_SIZE_OPTIONS,
} from "./constants.js";
import type { PlaceholderPick } from "./placeholderPick.js";
import {
  CURRENT_SELECTION_ROW_SX,
  CurrentSelectionChip,
} from "./CurrentSelectionChip.js";
import {
  TemplatePlaceholderSelectionPanel_ItemFragment,
  useTemplatePlaceholderSelectionPanel_OriginTemplateQuery,
} from "./TemplatePlaceholderSelectionPanel.generated.js";

const ALLOWED_VIEWS: TemplatesCollectionView[] = ["list", "gallery", "table"];

interface Props {
  projectId: string | null;
  docId: string;
  item: TemplatePlaceholderSelectionPanel_ItemFragment;
  refetchWizardQuestions: () => Promise<unknown>;
  handleClose: () => void;
  /**
   * The placeholder was filled. Distinct from `handleClose`, which the search
   * surface also calls on a plain dismiss: a surface that steps on an answer
   * needs the two apart.
   */
  onApplied?: (pick: PlaceholderPick) => Promise<void>;
}

export const TemplatePlaceholderSelectionPanel = ({
  projectId,
  docId,
  item,
  refetchWizardQuestions,
  handleClose,
  onApplied,
}: Props) => {
  const { t } = useTranslate(["TemplatesSearch"]);
  const { enqueueSnackbar } = useSnackbar();
  const client = useApolloClient();

  const [setItemTemplatePlaceholderTemplate] =
    useSetItemTemplatePlaceholderTemplateMutation({ client });

  const initialFilters = React.useMemo(
    () => filterDefinitionsToFormValues(item.templateSearchFilterDefaults),
    [item.templateSearchFilterDefaults]
  );
  const defaultSorting = React.useMemo(
    () => sortingDefinitionsToFormValues(item.templateSearchSortingDefaults),
    [item.templateSearchSortingDefaults]
  );
  const initialSorting = React.useMemo(
    () => searchQuotesSortingToQuoteTemplatesSorting(defaultSorting),
    [defaultSorting]
  );
  // Property sortings have no grid column, so the column conversion above drops
  // them; they are appended to the query after the column sort instead.
  const seedPropertySortings = React.useMemo(
    () => searchQuotesSortingToPropertySortings(defaultSorting),
    [defaultSorting]
  );
  const fixedFilters = React.useMemo(
    () => ({
      applicableFor: ["QUOTE" as const],
      templateTypeIds: item.templateSearchFilterDefaults.templateTypeIdFilter
        ? [item.templateSearchFilterDefaults.templateTypeIdFilter.valueId]
        : null,
    }),
    [item.templateSearchFilterDefaults.templateTypeIdFilter]
  );

  const {
    queryBaseVariables,
    filters,
    setFilters,
    resetFilters,
    sorting,
    setSorting,
    paginationModel,
    setPaginationModel,
    searchTerm,
    setSearchTerm,
  } = useTemplatesQueryVariablesInState({
    initialFilters,
    initialSorting,
    initialSearchTerm: "",
    perPageDefaultAmount: PLACEHOLDER_SELECTION_PAGE_SIZE,
  });

  // An unfilled placeholder can still carry the origin of the template it was
  // cloned from, which is not a choice made for this slot.
  const filledFromId = item.isTemplatePlaceholderFilled
    ? (item.templateOriginQuoteId ?? null)
    : null;

  // Marks the template this placeholder was filled from, when it is among the
  // results. Only the id is read by `TemplatesDisplay`.
  const selectedTemplate = React.useMemo(
    () => (filledFromId ? { id: filledFromId } : null),
    [filledFromId]
  );

  const originQuery = useTemplatePlaceholderSelectionPanel_OriginTemplateQuery({
    client,
    variables: { id: filledFromId ?? "" },
    skip: !filledFromId,
    // An unreadable origin just pins nothing; the panel opens on its own.
    context: { noErrorNotification: true },
  });
  const originTemplate = getDataOrNull(
    originQuery.data?.quoteTemplateLatestAvailableVersion
  )?.quoteTemplate;
  const [inspectingOrigin, setInspectingOrigin] = React.useState(false);

  // Ignores repeat picks until the fill, the wizard refetch and the step have settled.
  const applying = React.useRef(false);
  const handleTemplateSelect = async (template: TemplateBase) => {
    if (!template.resolvedAsReadModelVersionNumber || applying.current) return;
    applying.current = true;
    try {
      await setItemTemplatePlaceholderTemplate({
        variables: {
          input: {
            docId,
            itemId: item.id,
            projectId,
            templateQuoteId: template.id,
            templateQuoteIdAtVersionNumber:
              template.resolvedAsReadModelVersionNumber,
          },
        },
      });
      await refetchWizardQuestions();
      await onApplied?.({
        kind: "template",
        templateId: template.id,
        title: template.title,
      });
      handleClose();
    } catch (error) {
      if (error instanceof Error) {
        if (error.message.includes("Cycle in templates detected"))
          enqueueSnackbar(
            t("Creating cycle linking in templates is not allowed", {
              ns: "TemplatesSearch",
            }),
            { variant: "error" }
          );
        else enqueueSnackbar(error.message, { variant: "error" });
      }
    } finally {
      applying.current = false;
    }
  };

  return (
    <Stack direction="column" spacing={2}>
      {originTemplate && (
        <>
          <TemplateListItem
            template={originTemplate}
            selected
            StatusIcons={[<CurrentSelectionChip key="current-selection" />]}
            sx={CURRENT_SELECTION_ROW_SX}
            onInfoClick={() => setInspectingOrigin(true)}
          />
          {inspectingOrigin && (
            <TemplatesQuoteOverviewModal
              docId={originTemplate.id}
              versionNumber={originTemplate.resolvedAsReadModelVersionNumber}
              handleClose={() => setInspectingOrigin(false)}
            />
          )}
        </>
      )}
      <TemplatesDisplay
        allowedViews={ALLOWED_VIEWS}
        collectionViewPersistenceKey="template-placeholder-selection"
        defaultCollectionViewDesktop="list"
        fixedFilters={fixedFilters}
        queryBaseVariables={queryBaseVariables}
        filters={filters}
        setFilters={setFilters}
        resetFilters={resetFilters}
        sorting={sorting}
        setSorting={setSorting}
        seedPropertySortings={seedPropertySortings}
        paginationModel={paginationModel}
        setPaginationModel={setPaginationModel}
        pageSizeOptions={PLACEHOLDER_SELECTION_PAGE_SIZE_OPTIONS}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        onTemplateClick={handleTemplateSelect}
        onInspectedTemplateClick={handleTemplateSelect}
        selectedTemplate={selectedTemplate}
      />
    </Stack>
  );
};
